import { _log, lerp } from "@utils/utils";

export const FrequencyRanges = {
  bass: { name: "bass", color: "pink", low: 0, high: 140 },
  lowMid: { name: "lowMid", color: "orange", low: 140, high: 400 },
  mid: { name: "mid", color: "yellow", low: 400, high: 2600 },
  highMid: { name: "highMid", color: "cyan", low: 2600, high: 5200 },
  treble: { name: "treble", color: "tomato", low: 5200, high: 14000 },
} as const;

export function getEnergyAtHz(hz: number, frequencyBinCount: number, sampleRate: number) {
  const nyquist = sampleRate / 2;
  const maxVal = Math.min(frequencyBinCount - 1, Math.floor((hz / nyquist) * frequencyBinCount));
  return Math.max(0, maxVal);
}

export function CalcEnergies(low: number, high: number, freqData: Uint8Array, sampleRate: number) {
  const lowIndex = getEnergyAtHz(low, freqData.length, sampleRate);
  const highIndex = getEnergyAtHz(high, freqData.length, sampleRate);

  const buffer = freqData;
  let total = 0;
  let numFrequencies = 0;
  // add up all of the values for the frequencies
  for (let i = lowIndex; i <= highIndex; i++) {
    total += buffer[i];
    numFrequencies++;
  }
  // divide by total number of frequencies
  const toReturn = total / numFrequencies;
  return toReturn;
}

export function CalcEnergyBins(
  logarithmic = false,
  n: number,
  minFreq: number,
  maxFreq: number,
  freqData: Uint8Array,
  sampleRate: number,
) {
  const minFrequency = minFreq || 0;
  const maxFrequency = maxFreq || sampleRate / 2;
  const logBase = 10;
  const minFrequencyLog = _log(Math.max(1, minFrequency), logBase);
  const maxFrequencyLog = _log(Math.max(1, maxFrequency), logBase);

  const bands = [];
  for (let i = 0; i < n; i++) {
    const minT = i / n;
    const maxT = minT + 1 / n;
    let minHz, maxHz;
    if (logarithmic) {
      minHz = Math.pow(logBase, lerp(minFrequencyLog, maxFrequencyLog, minT));
      maxHz = Math.pow(logBase, lerp(minFrequencyLog, maxFrequencyLog, maxT));
    } else {
      minHz = lerp(minFrequency, maxFrequency, minT);
      maxHz = lerp(minFrequency, maxFrequency, maxT);
    }
    const energy = CalcEnergies(minHz, maxHz, freqData, sampleRate);
    bands.push(energy);
  }
  return bands;
}

export function EnergyBinRanges(
  logarithmic = false,
  n: number,
  minFreq: number,
  maxFreq: number,
  sampleRate: number,
): { low: number; high: number }[] {
  const minFrequency = minFreq || 0;
  const maxFrequency = maxFreq || sampleRate / 2;
  const logBase = 10;
  const minFrequencyLog = _log(Math.max(1, minFrequency), logBase);
  const maxFrequencyLog = _log(Math.max(1, maxFrequency), logBase);
  return Array.from({ length: n }, (_, i) => {
    const minT = i / n;
    const maxT = minT + 1 / n;
    return logarithmic
      ? {
        low: Math.pow(logBase, lerp(minFrequencyLog, maxFrequencyLog, minT)),
        high: Math.pow(logBase, lerp(minFrequencyLog, maxFrequencyLog, maxT)),
      }
      : {
        low: lerp(minFrequency, maxFrequency, minT),
        high: lerp(minFrequency, maxFrequency, maxT),
      };
  });
}
